import { Link } from 'react-router-dom';
import { formatSessionWallTime, formatSpecialization } from '@lighthouse/shared';

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');
}

function specialtyLine(values: string[]): string {
  const [first, second] = values;
  return [first, second]
    .filter((item): item is string => Boolean(item))
    .map((item) => formatSpecialization(item))
    .join(' · ');
}

export function TherapistPhotoCard({
  id,
  displayName,
  specializations,
  profilePhotoUrl,
  hourlyRateNgn,
  ratingAvg = 0,
  ratingCount = 0,
  nextSlot,
  featured = false,
  to,
  layout = 'photo',
}: {
  id: string;
  displayName: string;
  specializations: string[];
  profilePhotoUrl?: string | null;
  hourlyRateNgn: number;
  ratingAvg?: number;
  ratingCount?: number;
  nextSlot?: string;
  featured?: boolean;
  to?: string;
  layout?: 'photo' | 'row';
}) {
  const focus = specialtyLine(specializations);
  const href = to ?? `/therapists/${id}`;
  if (layout === 'row') {
    return (
      <Link
        to={href}
        className="flex items-center gap-3 rounded-2xl bg-white p-3 shadow-[0_4px_16px_rgba(28,25,23,0.08)]"
      >
        {profilePhotoUrl ? (
          <img
            src={profilePhotoUrl}
            alt=""
            className="h-16 w-16 rounded-2xl object-cover object-[center_34%]"
          />
        ) : (
          <span className="bg-glacier text-midnight flex h-16 w-16 items-center justify-center rounded-2xl font-serif text-xl">
            {initials(displayName) || '?'}
          </span>
        )}
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-1 font-bold text-[#1C1917]">
            <span className="truncate">{displayName}</span>
            <span className="text-[#1F7A4D]">
              <CheckIcon />
            </span>
          </span>
          <span className="text-slateink mt-0.5 block truncate text-xs">
            {focus || 'Verified clinician'}
          </span>
          {ratingCount > 0 ? (
            <span className="mt-1 block text-xs font-semibold text-[#C9A227]">
              ★ {ratingAvg.toFixed(1)} ({ratingCount})
            </span>
          ) : (
            <span className="text-slateink mt-1 block text-xs">No reviews yet</span>
          )}
          <span className="mt-0.5 block text-[13px] font-bold text-[#1C1917]">
            ₦{hourlyRateNgn.toLocaleString('en-NG')} / session
          </span>
        </span>
        <ChevronIcon />
      </Link>
    );
  }
  const nextLabel = nextSlot
    ? formatSessionWallTime(nextSlot, { hour: 'numeric', minute: '2-digit' })
    : '';

  return (
    <Link
      to={href}
      className="flex w-64 shrink-0 snap-start flex-col rounded-[22px] bg-white p-2.5 shadow-[0_4px_12px_rgba(28,25,23,0.06)]"
    >
      <div className="relative aspect-square overflow-hidden rounded-2xl bg-[#EFE8DC]">
        {profilePhotoUrl ? (
          <img
            src={profilePhotoUrl}
            alt=""
            className="h-full w-full object-cover object-[center_34%]"
          />
        ) : (
          <div className="text-primary-700 flex h-full items-center justify-center font-serif text-3xl">
            {initials(displayName) || '?'}
          </div>
        )}
        <span className="absolute bottom-2 right-2 inline-flex items-center gap-1 rounded-full bg-white/95 px-2 py-0.5 text-[11px] font-bold text-[#1F7A4D]">
          <CheckIcon />
          Verified
        </span>
        {featured ? (
          <span className="bg-glacier text-midnight absolute left-2 top-2 rounded-md px-1.5 py-0.5 text-[9px] font-bold">
            Featured
          </span>
        ) : null}
      </div>
      <p className="mt-2.5 truncate text-base font-bold text-[#1C1917]">{displayName}</p>
      <p className="text-slateink truncate text-xs">{focus || 'Verified clinician'}</p>
      {ratingCount > 0 ? (
        <p className="mt-1.5 text-xs">
          <span className="text-[#C9A227]">★ </span>
          <span className="font-bold text-[#1C1917]">{ratingAvg.toFixed(1)} </span>
          <span className="text-[#A8A29E]">({ratingCount} reviews)</span>
        </p>
      ) : (
        <p className="mt-1.5 text-xs text-[#A8A29E]">No reviews yet</p>
      )}
      {nextSlot ? (
        <p className="text-slateink mt-2 flex items-center gap-1.5 text-xs">
          <CalendarIcon />
          <span className="min-w-0 flex-1 truncate">Next: Today, {nextLabel}</span>
          <ChevronIcon />
        </p>
      ) : null}
      <p className="mt-1.5 flex items-center gap-1.5 text-[13px] font-bold text-[#1C1917]">
        <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-[#E5F3EA] text-[10px] font-extrabold text-[#1F7A4D]">
          ₦
        </span>
        <span>
          ₦{hourlyRateNgn.toLocaleString('en-NG')}
          <span className="font-medium text-[#A8A29E]"> / session</span>
        </span>
      </p>
    </Link>
  );
}

function CheckIcon() {
  return (
    <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M12 2a10 10 0 100 20 10 10 0 000-20zm-1.2 14.2l-3.5-3.5 1.4-1.4 2.1 2.1 4.5-4.5 1.4 1.4-5.9 5.9z" />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden>
      <rect x="4" y="5" width="16" height="15" rx="2" strokeWidth="1.75" />
      <path d="M8 3.5V7M16 3.5V7M4 10h16" strokeWidth="1.75" strokeLinecap="round" />
    </svg>
  );
}

function ChevronIcon() {
  return (
    <svg
      className="h-4 w-4 text-[#A8A29E]"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      aria-hidden
    >
      <path d="M9 6l6 6-6 6" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
